import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  ChevronLeft, ChevronRight, Lock, RefreshCcw, Calendar as CalIcon,
} from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { useAuth } from "../auth";
import { useT } from "../i18n";
import { katalog } from "../i18n/lang";
import { TaskDrawer } from "../components/TaskDrawer";
import { Button, StatusBadge, statusLabel } from "../ui";

interface CalTask {
  id: string;
  project_id: string | null;
  title: string;
  status: string | null;
  start_date: string | null;
  due_date: string;
  source: "local" | "salesforce";
  sf_url: string | null;
  read_only: boolean;
}

interface CalProject {
  id: string;
  name: string;
}

// Map task.status → calendar color class
function chipColor(status: string | null, source: string): string {
  const s = (status ?? "").toLowerCase();
  if (s === "done" || s === "closed") return "bg-surface-3 text-text opacity-60";
  if (s === "lost") return "bg-danger-soft text-danger opacity-70";
  if (s === "on hold") return "bg-warning-soft text-warning";
  if (s === "in_progress" || s === "ongoing") return "bg-brand-200 dark:bg-brand-900/40 text-brand-900 dark:text-brand-200";
  return source === "salesforce" ? "bg-cream-200 dark:bg-ink-700 text-ink-50" : "bg-accent-100 text-accent-800";
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function monthGrid(year: number, month: number): Date[] {
  // Returns 35 or 42 days covering the full visible month grid (Mon-first)
  const first = new Date(year, month, 1);
  const dayOfWeek = (first.getDay() + 6) % 7; // 0=Mon
  const start = new Date(year, month, 1 - dayOfWeek);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    days.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  // Trim trailing week if entirely next-month
  while (days.length > 35 && days[days.length - 1].getMonth() !== month && days[days.length - 7].getMonth() !== month) {
    days.splice(-7);
  }
  return days;
}

const DAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const DAY_LABELS_LONG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

// Build N consecutive days from start (inclusive).
function daysFrom(start: Date, count: number): Date[] {
  const out: Date[] = [];
  for (let i = 0; i < count; i++) {
    out.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return out;
}

export function CalendarView({ embedded = false }: { embedded?: boolean } = {}) {
  const t = useT();
  const { username } = useAuth();
  const [cursor, setCursor] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  // Mobile-Agenda: separates Window-Start, default = heute. Header-Nav
  // (Sub 2) shiftet um +/-14 Tage. Desktop bleibt cursor (month-start).
  const [agendaStart, setAgendaStart] = useState<Date>(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
  });
  const [tasks, setTasks] = useState<CalTask[]>([]);
  const [projects, setProjects] = useState<Record<string, string>>({});
  const [includeDone, setIncludeDone] = useState(true);
  const [includeSf, setIncludeSf] = useState(true);
  const [myOnly, setMyOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawerTask, setDrawerTask] = useState<{ id: string; source: "local" | "salesforce"; sf_url?: string | null } | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const days = useMemo(() => monthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Pull tasks with due_date in the visible range. Reuse /views/tasks
      // which returns the union (local + SF). Filter to due_date in range.
      const [taskRows, projRows] = await Promise.all([
        api.get<CalTask[]>("/views/tasks"),
        api.get<{ projects: CalProject[] }>("/views/projects"),
      ]);
      setTasks(taskRows.filter((t) => t.due_date));
      const map: Record<string, string> = {};
      for (const p of projRows.projects) map[p.id] = p.name;
      setProjects(map);
    } catch (e: any) {
      // `katalog()` statt `t`: `load` haengt an KEINER Abhaengigkeit (und soll
      // beim Sprachwechsel auch nicht neu laden) — hier zaehlt die Sprache im
      // Moment des Fehlers, nicht die beim Anlegen der Funktion.
      setError(e?.message || katalog().calendar.aufgaben.ladenFehlgeschlagen);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter + group tasks by day
  const tasksByDay = useMemo(() => {
    const m: Record<string, CalTask[]> = {};
    for (const t of tasks) {
      if (!t.due_date) continue;
      if (!includeDone && (t.status === "done" || t.status === "Closed" || t.status === "closed")) continue;
      if (!includeSf && t.source === "salesforce") continue;
      // For now no owner field on /views/tasks — myOnly only applies to local; SF has no owner
      if (myOnly && t.source === "local" && (t as any).owner !== username) continue;
      (m[t.due_date] ||= []).push(t);
    }
    return m;
  }, [tasks, includeDone, includeSf, myOnly, username]);

  const handleDragEnd = async (e: DragEndEvent) => {
    const taskId = e.active.id as string;
    const targetDay = e.over?.id as string | undefined;
    if (!targetDay) return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.read_only) {
      if (task?.read_only) {
        setError(t.calendar.aufgaben.sfNichtVerschiebbar);
        setTimeout(() => setError(null), 4000);
      }
      return;
    }
    if (task.due_date === targetDay) return;
    // Optimistic
    setTasks((all) => all.map((t) => (t.id === taskId ? { ...t, due_date: targetDay } : t)));
    try {
      await api.patch(`/tasks/${taskId}`, { due_date: targetDay });
    } catch (err: any) {
      setError(t.calendar.aufgaben.verschiebenFehlgeschlagen(err?.message || "?"));
      await load();
    }
  };

  const today = ymd(new Date());
  // „de-DE" statt „de" — identische Ausgabe, aber dieselbe Schreibweise wie
  // überall sonst (ein Sweep nach dem Locale-Argument findet sonst zwei Formen).
  const monthLabel = cursor.toLocaleString("de-DE", { month: "long", year: "numeric" });

  // Mobile-Agenda-Range-Label: "TT.MM. - TT.MM."
  const agendaEnd = new Date(agendaStart.getFullYear(), agendaStart.getMonth(), agendaStart.getDate() + 13);
  const fmtDM = (d: Date) => `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
  const agendaRangeLabel = `${fmtDM(agendaStart)} - ${fmtDM(agendaEnd)}`;
  const shiftAgenda = (days: number) =>
    setAgendaStart(new Date(agendaStart.getFullYear(), agendaStart.getMonth(), agendaStart.getDate() + days));
  const goToday = () => {
    const t = new Date();
    setAgendaStart(new Date(t.getFullYear(), t.getMonth(), t.getDate()));
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
  };

  return (
    <div className="p-3 md:p-6">
      {/* Title-Row: immer sichtbar, Refresh rechts. */}
      <div className="flex items-center gap-3 mb-3">
        {!embedded && (
          <h1 className="text-xl md:text-2xl font-display font-semibold flex items-center gap-2">
            <CalIcon className="w-5 h-5" />
            {t.calendar.aufgaben.titel}
          </h1>
        )}
        <span className="text-xs text-ink-50/60 hidden md:inline">{t.calendar.aufgaben.perDragVerschieben}</span>
        <button
          onClick={load}
          disabled={loading}
          className="ml-auto rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700 flex items-center justify-center min-h-11 min-w-11 md:min-h-0 md:min-w-0 md:p-1.5"
          title={t.calendar.aufgaben.neuLaden}
          aria-label={t.calendar.aufgaben.neuLaden}
        >
          <RefreshCcw className={clsx("w-4 h-4", loading && "animate-spin")} />
        </button>
      </div>

      {/* Mobile Nav-Row: 14-day-shift + Range-Label + today. */}
      <div className="md:hidden flex items-center gap-2 mb-3">
        <button
          onClick={() => shiftAgenda(-14)}
          className="rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700 flex items-center justify-center min-h-11 min-w-11"
          title={t.calendar.aufgaben.zurueck14Tage}
          aria-label={t.calendar.aufgaben.zurueck14Tage}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 text-center text-sm font-medium tabular-nums">{agendaRangeLabel}</div>
        <button
          onClick={() => shiftAgenda(14)}
          className="rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700 flex items-center justify-center min-h-11 min-w-11"
          title={t.calendar.aufgaben.vor14Tage}
          aria-label={t.calendar.aufgaben.vor14Tage}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <Button variant="ghost" size="sm" onClick={goToday} className="min-h-11">
          {t.calendar.aufgaben.heuteKlein}
        </Button>
      </div>

      {/* Mobile Filter-Row: 3 Toggle-Buttons mit min-h-11 */}
      <div className="md:hidden flex items-center gap-1.5 mb-3 text-xs overflow-x-auto">
        <FilterToggle label={t.calendar.aufgaben.nurMeine} checked={myOnly} onChange={setMyOnly} />
        <FilterToggle label={t.calendar.aufgaben.erledigte} checked={includeDone} onChange={setIncludeDone} />
        <FilterToggle label={t.calendar.aufgaben.salesforce} checked={includeSf} onChange={setIncludeSf} />
      </div>

      {/* Desktop Filter-+ Month-Nav-Row */}
      <div className="hidden md:flex items-center gap-2 mb-4 flex-wrap text-sm">
        <button
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          className="p-1.5 rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700"
          title={t.calendar.aufgaben.vorherigerMonat}
          aria-label={t.calendar.aufgaben.vorherigerMonat}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="text-sm font-medium min-w-[120px] text-center">{monthLabel}</div>
        <button
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          className="p-1.5 rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700"
          title={t.calendar.aufgaben.naechsterMonat}
          aria-label={t.calendar.aufgaben.naechsterMonat}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <Button variant="ghost" size="sm" onClick={goToday}>
          {t.calendar.aufgaben.heute}
        </Button>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={myOnly} onChange={(e) => setMyOnly(e.target.checked)} />
          {t.calendar.aufgaben.nurMeine}
        </label>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />
          {t.calendar.aufgaben.erledigte}
        </label>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={includeSf} onChange={(e) => setIncludeSf(e.target.checked)} />
          {t.calendar.aufgaben.salesforce}
        </label>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-soft border border-danger/30 rounded-md px-3 py-2 mb-3">
          {error}
        </div>
      )}

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        {/* Desktop: 7-Spalten-Monats-Grid (md+). Mobile rendert stattdessen
            AgendaList unten — kein Drag noetig dort. */}
        <div className="hidden md:block">
          {/* Day labels */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAY_LABELS.map((d) => (
              <div key={d} className="text-xs uppercase tracking-wider text-ink-50/50 px-1.5 py-1 text-center">{d}</div>
            ))}
          </div>
          {/* Grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const dStr = ymd(d);
              const isCurrentMonth = d.getMonth() === cursor.getMonth();
              const isToday = dStr === today;
              const dayTasks = tasksByDay[dStr] || [];
              return (
                <DayCell
                  key={dStr}
                  day={d}
                  dStr={dStr}
                  isCurrentMonth={isCurrentMonth}
                  isToday={isToday}
                  tasks={dayTasks}
                  projects={projects}
                  onClickTask={(t) => setDrawerTask({ id: t.id, source: t.source, sf_url: t.sf_url })}
                />
              );
            })}
          </div>
        </div>
      </DndContext>

      {/* Mobile: vertikale 14-Day-Agenda */}
      <div className="md:hidden">
        <AgendaList
          start={agendaStart}
          tasksByDay={tasksByDay}
          projects={projects}
          today={today}
          onClickTask={(t) => setDrawerTask({ id: t.id, source: t.source, sf_url: t.sf_url })}
        />
      </div>

      <TaskDrawer
        taskInfo={drawerTask}
        onClose={() => setDrawerTask(null)}
        onChanged={load}
      />
    </div>
  );
}

function DayCell({
  day, dStr, isCurrentMonth, isToday, tasks, projects, onClickTask,
}: {
  day: Date;
  dStr: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  tasks: CalTask[];
  projects: Record<string, string>;
  onClickTask: (t: CalTask) => void;
}) {
  const txt = useT();
  const { setNodeRef, isOver } = useDroppable({ id: dStr });
  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "min-h-[110px] rounded-md border p-1.5 flex flex-col gap-0.5 transition-colors",
        isCurrentMonth
          ? "bg-cream-50 dark:bg-ink-800 border-cream-200 dark:border-ink-700"
          : "bg-cream-100/40 dark:bg-ink-900/40 border-cream-100 dark:border-ink-800",
        isOver && "outline outline-2 outline-brand-400 bg-brand-50 dark:bg-brand-900/20",
      )}
    >
      <div className={clsx(
        "flex items-baseline justify-between text-[11px] tabular-nums",
        isCurrentMonth ? "text-ink-50" : "text-ink-50/40",
      )}>
        <span className={clsx(
          isToday && "bg-brand-500 text-white rounded-full w-5 h-5 inline-flex items-center justify-center font-medium",
        )}>{day.getDate()}</span>
        {tasks.length > 3 && (
          <span className="text-[9px] text-ink-50/50 tabular-nums">{txt.calendar.aufgaben.anzahlAufgaben(tasks.length)}</span>
        )}
      </div>
      <div className="flex flex-col gap-0.5 overflow-hidden">
        {tasks.slice(0, 5).map((t) => (
          <TaskChip
            key={t.id}
            task={t}
            project={t.project_id ? projects[t.project_id] : null}
            onClick={() => onClickTask(t)}
          />
        ))}
        {tasks.length > 5 && (
          <div className="text-[9px] text-ink-50/50 text-center">{txt.calendar.aufgaben.weitere(tasks.length - 5)}</div>
        )}
      </div>
    </div>
  );
}

function FilterToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (b: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={clsx(
        "min-h-11 px-3 rounded-full text-xs font-medium border transition-colors shrink-0",
        checked
          ? "bg-brand-500 text-white border-brand-500"
          : "bg-cream-50 dark:bg-ink-800 text-ink-50 border-cream-300 dark:border-ink-600"
      )}
      aria-pressed={checked}
    >
      {label}
    </button>
  );
}

function AgendaList({
  start, tasksByDay, projects, today, onClickTask,
}: {
  start: Date;
  tasksByDay: Record<string, CalTask[]>;
  projects: Record<string, string>;
  today: string;
  onClickTask: (t: CalTask) => void;
}) {
  const txt = useT();
  const days = useMemo(() => daysFrom(start, 14), [start]);
  return (
    <div className="space-y-1">
      {days.map((d) => {
        const dStr = ymd(d);
        const isToday = dStr === today;
        const isPast = dStr < today;
        const dayTasks = tasksByDay[dStr] || [];
        const weekday = DAY_LABELS_LONG[(d.getDay() + 6) % 7];
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        return (
          <section
            key={dStr}
            className={clsx(
              "rounded-md border",
              isToday
                ? "border-brand-300 dark:border-brand-700 bg-brand-50/30 dark:bg-brand-900/10"
                : isPast
                ? "border-cream-200/50 dark:border-ink-700/50 bg-cream-50/40 dark:bg-ink-900/30 opacity-70"
                : "border-cream-200 dark:border-ink-700 bg-cream-50 dark:bg-ink-800"
            )}
          >
            {/* Tag-Header: TT.MM Wochentag + Task-Count */}
            <header className="flex items-baseline gap-2 px-3 py-2 border-b border-cream-200/60 dark:border-ink-700/60">
              <span
                className={clsx(
                  "text-base font-semibold tabular-nums",
                  isToday ? "text-brand-700 dark:text-brand-300" : "text-ink"
                )}
              >
                {dd}.{mm}
              </span>
              <span className="text-xs text-ink-50/60">{weekday}</span>
              {isToday && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-brand-500 text-white">{txt.calendar.aufgaben.heuteKlein}</span>
              )}
              <span className="ml-auto text-[10px] text-ink-50/50 tabular-nums">
                {dayTasks.length > 0 ? `${dayTasks.length}` : ""}
              </span>
            </header>
            <div className="px-2 py-1.5">
              {dayTasks.length === 0 ? (
                <div className="text-[11px] italic text-ink-50/40 text-center py-1">
                  {txt.calendar.aufgaben.keineTermine}
                </div>
              ) : (
                <ul className="space-y-1">
                  {dayTasks.map((t) => (
                    <li key={t.id}>
                      <AgendaItem
                        task={t}
                        project={t.project_id ? projects[t.project_id] : null}
                        onClick={() => onClickTask(t)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function AgendaItem({ task, project, onClick }: { task: CalTask; project: string | null; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left min-h-11 rounded-md px-2 py-2 flex items-center gap-2 hover:bg-cream-100 dark:hover:bg-ink-700 transition-colors"
    >
      {task.source === "salesforce" && <Lock className="w-3 h-3 shrink-0 opacity-60" />}
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className="text-sm text-ink break-words">{task.title}</span>
        {project && (
          <span className="text-[10px] text-ink-50/60 truncate">{project}</span>
        )}
      </div>
      {task.status ? (
        <StatusBadge status={statusLabel(task.status).status} className="shrink-0">
          {statusLabel(task.status).label}
        </StatusBadge>
      ) : (
        <StatusBadge status="neutral" className="shrink-0">
          {task.source === "salesforce" ? "SF" : "—"}
        </StatusBadge>
      )}
    </button>
  );
}

function TaskChip({ task, project, onClick }: { task: CalTask; project: string | null; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    disabled: task.read_only,
  });
  const cls = chipColor(task.status, task.source);
  return (
    <button
      type="button"
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => { if (!isDragging) onClick(); }}
      className={clsx(
        "w-full text-left rounded-md px-1.5 py-0.5 text-[10px] leading-tight cursor-pointer truncate flex items-center gap-1",
        cls,
        task.read_only ? "cursor-pointer" : "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-50",
      )}
      title={project ? `${project} · ${task.title}` : task.title}
    >
      {task.source === "salesforce" && <Lock className="w-2.5 h-2.5 shrink-0 opacity-60" />}
      <span className="truncate">{task.title}</span>
    </button>
  );
}

/** Direkt-Route-Hülle (Bestand). Der Inhalt lebt in <CalendarView/>. */
export default function CalendarPage() {
  return <CalendarView />;
}
